import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAsyncData } from "@/hooks/useAsyncData";
import { getProvenResultById } from "@/data/repository";
import { ErrorState } from "@/components/shared/DataState";
import CTASection from "@/components/sections/CTASection";
import { ArrowLeft, TrendingUp } from "lucide-react";

function DetailSkeleton() {
  return (
    <div className="container-page py-16 animate-pulse" data-testid="loading-state">
      <div className="h-4 w-28 bg-bronze/10 mb-8" />
      <div className="h-10 w-3/4 bg-bronze/15 mb-4" />
      <div className="aspect-[16/9] bg-bronze/10 mt-6" />
      <div className="mt-8 space-y-3">
        <div className="h-4 w-full bg-bronze/10" />
        <div className="h-4 w-5/6 bg-bronze/10" />
      </div>
    </div>
  );
}

export default function ProvenResultDetailPage() {
  const { id } = useParams();
  const { data: c, loading, error, reload } = useAsyncData(
    () => getProvenResultById(id),
    [id],
    null
  );

  if (loading) return <DetailSkeleton />;
  if (error)
    return (
      <div className="container-page py-16">
        <ErrorState onRetry={reload} />
      </div>
    );

  if (!c) {
    return (
      <div className="container-page py-24 text-center" data-testid="case-not-found">
        <TrendingUp className="mx-auto text-bronze" size={36} />
        <h1 className="font-serif text-4xl text-ink mt-5">Case study not found</h1>
        <p className="text-ink_soft/70 mt-3">This story may have been moved or unpublished.</p>
        <Link to="/success-stories/proven-results" className="btn-primary mt-7 inline-block">
          Back to Proven Results
        </Link>
      </div>
    );
  }

  const hero = c.image || (Array.isArray(c.images) ? c.images[0] : "");
  const rest = (Array.isArray(c.images) ? c.images : []).filter((u) => u && u !== hero);
  const isHtml = /<[a-z][\s\S]*>/i.test(c.story || "");

  return (
    <div data-testid="page-proven-result-detail" className="bg-cream">
      <article className="container-page py-10 md:py-16 max-w-3xl">
        <Link
          to="/success-stories/proven-results"
          data-testid="back-to-proven-results"
          className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark"
        >
          <ArrowLeft size={14} /> All Case Studies
        </Link>

        {c.metric && (
          <div className="mt-7 inline-block bg-beige text-bronze px-3 py-1 text-[0.7rem] font-semibold tracking-[0.18em] uppercase">
            {c.metric}
          </div>
        )}

        <h1 className="mt-4 font-serif text-4xl md:text-5xl text-ink leading-tight">
          {c.headline}
        </h1>
        {(c.propertyType || c.sub) && (
          <p className="mt-3 text-[0.7rem] tracking-[0.22em] uppercase text-olive">
            {c.propertyType || c.sub}
          </p>
        )}

        {hero && (
          <div className="mt-8 aspect-[16/9] overflow-hidden bg-beige">
            <img src={hero} alt={c.headline} className="h-full w-full object-cover" />
          </div>
        )}

        {c.story && (
          isHtml ? (
            <div
              className="prose-editorial mt-10 text-base font-light leading-relaxed text-ink_soft/85 space-y-5"
              data-testid="case-story"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.story) }}
            />
          ) : (
            <p data-testid="case-story" className="mt-10 text-base font-light leading-relaxed text-ink_soft/85 whitespace-pre-line">
              {c.story}
            </p>
          )
        )}

        {rest.length > 0 && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="case-gallery">
            {rest.map((url) => (
              <div key={url} className="aspect-[4/3] overflow-hidden bg-beige border border-bronze/15">
                <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}
      </article>
      <CTASection />
    </div>
  );
}
